export interface IconMini {
  /**
   * The color of the icon
   */
  icon_color: string;
  /**
   * The material design icon name
   */
  icon_material_design_name: string;
}

export interface Portal {
  /** The id of the portal */
  portal_id: number;
  /** The name of the portal */
  name: string;
  /** Permissions on manage news*/
  can_manage_news: boolean;
  /** Permissions on management information */
  may_see_diagrams: boolean;
  /** Shows the browse button*/
  show_browse_button: boolean;
  /** Has news items enable/disable news menu*/
  has_content_items: boolean;
  /** Enable/disable fill out menu */
  has_fillable_contents: boolean;
  /** Indicates if the portal has any diagrams coupled */
  has_diagrams: boolean;
  /** Indicates if the portal has any reports coupled */
  has_reports: boolean;
  /** Indicates if the portal should display link to report management module */
  show_link_to_reporting: boolean;
  /** Organization unit for this portal */
  organization_unit_id: number;
  /** When the user last visited (if ever) this portal*/
  last_visited_date_time: string | null;
  /** Indicates if the portal has been setup to show important hyperlinks */
  show_important_hyperlinks_panel: boolean;
  /** Important hyperlinks panel title */
  important_hyperlinks_panel_title: string | null;
  /**Group of the portal */
  portal_group: PortalGroup;
  /** If the user can see the ID's of the documents along with the title */
  show_identifier: boolean;
  /** Identifier setting type */
  show_identifier_setting_type: IdentifierSettingType;
  //** The search feedback instruction text */
  search_feedback_instruction: string;
  //** The search feedback instruction setting type */
  search_feedback_instruction_setting_type: SearchFeedbackInstructionSettingType;
  //** Has tooltips fields set on portal settings*/
  has_tooltip_fields: boolean;
  /** The color of the portal */
  color: string;
  /** The background image of the portal */
  background_image_base64: string;

  /** default selected collection   */
  selected_collection?: CollectionMini;
  /** default expanded collections */
  expanded_collections?: CollectionMini[];
}

export interface CollectionMini {
  collection_id: number;
  name: string;
}

export interface PortalGroup {
  /** The id of the portal group*/
  portal_group_id: number;
  /** Name of the group  */
  name: string;
  /** Organization unit for this portal */
  order: number;
}

export enum IdentifierSettingType {
  /** Not modified by user*/
  Default,
  /** Modified by user*/
  Custom
}

export enum SearchFeedbackInstructionSettingType {
  /** Not modified by user*/
  Default,
  /** Modified by user*/
  Custom
}

export interface PortalMini {
  /** The id of the portal */
  portal_id: number;
  /** The name of the portal */
  name: string;
}

export interface User {
  /** The id (guid) of the user */
  user_id: string;
  /** The language (xx-XX) of the user */
  language: string;
  /** The name of the user */
  name: string;
  /** The login code of the user */
  login_code: string;
  /** The date format the user wishes to use
   *  with Y M and D
   * */
  date_format: string;
  /** The id of the time zone of the user */
  time_zone_id: string;
  /** The e-mail address of the user */
  email_address?: string;
  /** The phone number of the user */
  phone_number?: string;
  /** The usergroups the user is a member of */
  user_groups?: UserGroup[];
  /** The organization units the user is a member of */
  organization_units?: OrganizationUnit[];
  /** The manager of the user */
  manager?: Manager;
  /** User or group */
  account_type: AccountType;
  /** The start portal of the user */
  start_portal: PortalMini;
  /** Out of office status */
  out_of_office: OutOfOffice;
  /** Statistics */
  statistics?: Statistics;
  /** User type */
  user_type: UserType;
  /** Whether show tasks and subscriptions or not */
  show_tasks_and_subscriptions: boolean;
  /** Whether show favorites and recent panel or not */
  show_favorites_and_recent_panel: boolean;
  /** Whether show preferences or not */
  show_preferences: boolean;
  /** Used for 2FA QR code */
  two_step_otp_auth_url?: string;
  /** Whether or not 2-step authentication is switched on for the user */
  two_step_authentication_allowed?: boolean;
  /** The two step deadline provides a grace period between enabling two-factor for a user and activating it by the user */
  two_step_deadline_datetime?: string;
}

export interface Manager {
  /** The user id (guid) of the manager */
  manager_id: string;
  /** The name of the manager */
  name: string;
}

export interface OrganizationUnit {
  /** The id (number) of the organization unit */
  organization_unit_id: number;

  /** The name of the organization unit */
  name: string;

  /** The external id (string) of the organization unit (used to match when importing / exporting) */
  external_id: string;
}

export interface UserFilter {
  /** (Part of) the name to filter on */
  name?: string;
  /** (Part of) the login code to filter on */
  login_code?: string;
  /** A list of user ids (guid) to filter on */
  user_ids?: string[];
  /** A user group id (guid) to filter on */
  user_group_id?: string;
  /** Filter rules on email address of user **/
  email?: Condition<string>;
  /** (Part of) the name, login code or e-mail address to filter on **/
  name_logincode_email_contains?: string;
  /** Filter rules on active property of user **/
  active?: boolean;
}

export interface UserPatch {
  /** the login code */
  login_code?: string;
  /** the external id */
  external_id?: string;
  /** the source id */
  source_id?: string;
  /** whether two step activation is requested or not */
  two_step_activation_requested?: boolean;
  /** the new password */
  new_password?: string;
  /** the old password */
  old_password?: string;
  /** the start portal id of the user */
  start_portal_id?: number;
  /** The language (xx-XX) of the user */
  language: string;
  /** The date format */
  date_format: string;
  /** The id of the time zone of the user */
  time_zone_id?: string;
  /** Out of office status */
  out_of_office: OutOfOffice;
}

export interface RequestPassword {
  /** the new password */
  new_password: string;
}

export interface GetRequestPassword {
  /** the new password */
  id: string;
  is_valid: boolean;
}

export enum AccountType {
  personal = "personal",
  group = "group"
}

export interface OutOfOffice {
  from: string;
  to: string;
}

export interface Statistics {
  unread_notifications_amount: number;
  tasks_amount: number;
  notifications_amount: number;
  unread_tasks_amount: number;
  overdue_tasks_amount: number;
}

export enum UserType {
  none = "None",
  systemUser = "system_user",
  syncedUser = "synced_user",
  normalUser = "normal_user"
}

export interface Condition<T> {
  op: "op" | "co" | "pr";
  value?: T;
}

export interface UserGroup {
  /** The id (guid) of the group */
  user_group_id: string;
  /** The name of the group */
  name: string;
  /** The description of the group */
  description?: string;
  /** The members of the group */
  users?: UserGroupMember[];
}

export interface UserGroupMember {
  /** The user id (guid) of the group member */
  user_id: string;
  /** The name of the group member */
  name: string;
}

export interface UserGroupFilter {
  /** (Part of) the name to filter on */
  name?: string;
  /** A list of user group ids (guid) to filter on */
  user_group_ids?: string[];
  /** Whether to include system groups */
  include_system_groups?: boolean;
}
